import React from "react";
import { CameraOutlined } from "@ant-design/icons";
import { userPutChangeAvatar } from "@apis";
import { useToaster } from "@components";
import { useRouterStore } from "@stores";
import { comprimirImagen } from "@helpers";
import { AccountModalShell } from "./account-modal-shell";

interface ChangeAvatarModalProps {
  open: boolean;
  onClose: () => void;
}

const getInitials = (name?: string | null): string => {
  if (!name) return "U";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const MAX_BYTES = 2 * 1024 * 1024;

export const ChangeAvatarModal = ({ open, onClose }: ChangeAvatarModalProps) => {
  const toaster = useToaster();
  const firstName = useRouterStore((s) => s.firstName);
  const currentAvatar = useRouterStore((s) => s.avatar);
  const setAvatar = useRouterStore((s) => s.setAvatar);

  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [processing, setProcessing] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setProcessing(false);
  };

  const handleClose = () => {
    if (processing) return;
    reset();
    onClose();
  };

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      toaster.error({
        title: "Archivo inválido",
        message: "Selecciona una imagen (JPG o PNG)",
        duration: 4000,
        placement: "topRight",
      });
      return;
    }
    if (picked.size > MAX_BYTES) {
      toaster.error({
        title: "Imagen muy pesada",
        message: "La imagen no puede superar 2MB",
        duration: 4000,
        placement: "topRight",
      });
      return;
    }
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  const handleSubmit = async () => {
    if (!file || processing) return;
    try {
      setProcessing(true);
      const blob = (await comprimirImagen(file, 40)) as Blob;
      const base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const { data } = await userPutChangeAvatar(base64);
      // refresco optimista en el store (el src local sirve hasta el próximo login)
      setAvatar(preview ?? currentAvatar);
      toaster.success({
        title: "Listo",
        message: data ?? "Avatar actualizado",
        duration: 4000,
        placement: "topRight",
      });
      reset();
      onClose();
    } catch (err: any) {
      toaster.error({
        title: "Error",
        message: err?.message ?? "No se pudo actualizar el avatar",
        duration: 5000,
        placement: "topRight",
      });
      setProcessing(false);
    }
  };

  const previewSrc = preview ?? currentAvatar;

  return (
    <AccountModalShell
      open={open}
      onClose={handleClose}
      icon={<CameraOutlined />}
      title="Cambiar avatar"
      subtitle="Actualiza tu foto de perfil"
      footer={
        <>
          <button
            type="button"
            className="acct-btn acct-btn--ghost"
            onClick={handleClose}
            disabled={processing}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="acct-btn acct-btn--primary"
            onClick={handleSubmit}
            disabled={!file || processing}
          >
            {processing ? "Guardando…" : "Guardar cambios"}
          </button>
        </>
      }
    >
      <div className="acct-avatar-row">
        {previewSrc ? (
          <img className="acct-avatar-preview" src={previewSrc} alt="avatar" />
        ) : (
          <div className="acct-avatar-preview">{getInitials(firstName)}</div>
        )}

        <div className="acct-avatar-actions">
          <button
            type="button"
            className="acct-btn acct-btn--ghost"
            onClick={() => inputRef.current?.click()}
            disabled={processing}
          >
            Elegir imagen
          </button>
          <span className="acct-avatar-hint">JPG o PNG, máx 2MB</span>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        style={{ display: "none" }}
        onChange={handlePick}
      />
    </AccountModalShell>
  );
};
